import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

export interface LeadAction {
  id: number;
  lead_id: number;
  action_type: 'Call' | 'Quote' | 'Appointment Set' | 'Visit Booked' | 'Won' | 'Lost' | 'Update';
  description: string;
  action_date: string;
  action_time: string;
  created_at: string;
}

export interface Appointment {
  id: number;
  lead_id: number;
  appointment_type: string;
  appointment_date: string;
  appointment_time: string;
  assigned_user: string;
  notes: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  created_at: string;
  created_by: string;
}

export interface Lead {
  id: number;
  customer_id?: number;
  title: string;
  customer_name: string; // Keep for backward compatibility, but will be computed from first_name + last_name
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  home_phone?: string;
  address_1?: string;
  address_2?: string;
  address_3?: string;
  town?: string;
  postcode: string;
  latitude?: number | null;
  longitude?: number | null;
  source: string;
  status: 'new' | 'contacted' | 'quoted' | 'not_ready' | 'won' | 'lost' | 'no_reply';
  lost_status?: 'junk' | 'out_of_area' | 'new_spray' | 'no_reply' | 'not_interested' | 'wrong_number' | 'already_sorted' | 'price';
  priority: 'high' | 'medium' | 'low';
  estimated_value: number;
  notes?: string;
  created_at: string;
  next_follow_up: string;
  order_number?: string;
  introducer_id?: number | null;
  actions?: LeadAction[];
  appointments?: Appointment[];
}

interface LeadsContextType {
  leads: Lead[];
  leadsLoading: boolean;
  addLead: (lead: Lead) => void;
  updateLead: (id: number, updates: Partial<Lead>) => void;
  deleteLead: (id: number) => void;
  addAction: (leadId: number, action: LeadAction) => void;
  addAppointment: (leadId: number, appointment: Appointment) => void;
  getAllAppointments: () => Appointment[];
  refreshLeads: () => Promise<void>;
}

const LeadsContext = createContext<LeadsContextType | undefined>(undefined);

// No mock data - all data fetched from API

export function LeadsProvider({ children }: { children: ReactNode }) {
  // Always start with empty array - no localStorage caching to ensure fresh data
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState<boolean>(true);
  const [retryCount, setRetryCount] = useState(0);

  // Clear any old cached leads on mount
  useEffect(() => {
    localStorage.removeItem('cached_leads');
  }, []);

  // Fetch leads from API with retry logic - always fresh data
  const fetchLeads = async (attempt = 0): Promise<void> => {
    try {
      setLeadsLoading(true);

      const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
      const token = localStorage.getItem('token');

      // Explicitly add authorization header
      const response = await axios.get(`${API_BASE_URL}/leads?limit=1000`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });

      // Extract leads from response (API returns { leads: [...] })
      const leadsData = response.data.leads || [];

      setLeads(leadsData);
      setRetryCount(0);

    } catch (error: any) {
      // Don't log 401 errors - these are expected when not authenticated
      if (error.response?.status !== 401) {
        console.error(`LeadsContext: Error fetching leads (attempt ${attempt + 1}):`, error);
        console.error('LeadsContext: Error response:', error.response?.data);
      }

      // Don't retry on 401 errors
      if (error.response?.status === 401) {
        // User is not authenticated, stop trying
        return;
      }

      // Retry logic with exponential backoff for other errors
      if (attempt < 3) {
        const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
        setTimeout(() => {
          setRetryCount(attempt + 1);
          fetchLeads(attempt + 1);
        }, delay);
      }
    } finally {
      setLeadsLoading(false);
    }
  };

  // Fetch leads from API on mount only if authenticated
  useEffect(() => {
    // Only fetch if we have a token (user is authenticated)
    const token = localStorage.getItem('token');
    if (token) {
      fetchLeads();
    }
  }, []);

  // Listen for auth changes (login/logout)
  useEffect(() => {
    // Handle storage events from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token') {
        if (e.newValue) {
          // Token was added (user logged in) - fetch leads
          fetchLeads();
        } else {
          // Token was removed (user logged out) - clear leads
          setLeads([]);
        }
      }
    };

    // Handle login events from the same tab (custom event)
    const handleAuthLogin = () => {
      const token = localStorage.getItem('token');
      if (token) {
        fetchLeads();
      }
    };

    // Listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);
    // Listen for custom auth-login event from same tab
    window.addEventListener('auth-login', handleAuthLogin);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-login', handleAuthLogin);
    };
  }, []);

  const addLead = (lead: Lead) => {
    setLeads(prev => [...prev, lead]);
  };

  const updateLead = (id: number, updates: Partial<Lead>) => {
    setLeads(prev => prev.map(lead => 
      lead.id === id ? { ...lead, ...updates } : lead
    ));
  };

  const deleteLead = (id: number) => {
    setLeads(prev => prev.filter(lead => lead.id !== id));
  };

  const addAction = (leadId: number, action: LeadAction) => {
    setLeads(prev => prev.map(lead => 
      lead.id === leadId 
        ? { ...lead, actions: [...(lead.actions || []), action] }
        : lead
    ));
  };

  const addAppointment = (leadId: number, appointment: Appointment) => {
    setLeads(prev => prev.map(lead => 
      lead.id === leadId 
        ? { ...lead, appointments: [...(lead.appointments || []), appointment] }
        : lead
    ));
  };

  const getAllAppointments = () => {
    return leads.flatMap(lead => lead.appointments || []);
  };

  return (
    <LeadsContext.Provider value={{
      leads,
      leadsLoading,
      addLead,
      updateLead,
      deleteLead,
      addAction,
      addAppointment,
      getAllAppointments,
      refreshLeads: () => fetchLeads(),
    }}>
      {children}
    </LeadsContext.Provider>
  );
}

export function useLeads() {
  const context = useContext(LeadsContext);
  if (context === undefined) {
    throw new Error('useLeads must be used within a LeadsProvider');
  }
  return context;
}